import { CSSProperties, ReactNode } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import { HelpTextView } from '../popovers/help-text.view';
import { Color } from '@vivli/shared/theme';
import { faCopy, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCopyToClipboardHook } from '@vivli/shared/infrastructure/hook';
import { useToastApiContext } from '@vivli/core/infrastructure/context';

const containerStyle = (showCopyCol?: boolean, centered?: boolean): CSSProperties => ({
    display: 'grid',
    gridTemplateColumns: showCopyCol ? 'auto 1fr' : '1fr',
    columnGap: '5px',
    alignItems: 'center',
    textAlign: centered ? 'center' : 'left'
});

const linkStyle: CSSProperties = {
    color: Color.VIVLI_LIGHT_BLUE,
    textDecoration: 'underline',
    cursor: 'pointer'
};

const linkContainerStyle = (scrollable = true): CSSProperties => ({
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: '10px',
    alignItems: 'center',
    overflowX: 'hidden',
    overflowY: scrollable ? 'auto' : 'hidden',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    lineHeight: '18px'
});

interface GridCellLongTextComponentProps extends ICellRendererParams {
    allowCopy?: boolean;
    copyText?: string;
    onClick?: () => void;
    showPaperclipIcon?: boolean;
    dataId?: string;
    dataTestId?: string;
    titleText?: string;
    scrollable?: boolean;
    truncate?: boolean;
    centered?: boolean;
}

//note: don't use this where you could have array/list data - it will blow up
// - use GridCellList instead
export const GridCellLongTextView = ({
    value,
    valueFormatted,
    allowCopy,
    copyText,
    dataId,
    dataTestId,
    onClick,
    showPaperclipIcon = true,
    titleText,
    scrollable = true,
    truncate = false,
    centered = true
}: GridCellLongTextComponentProps): ReactNode => {
    const { copyToClipboard } = useCopyToClipboardHook();
    const toastService = useToastApiContext();

    const _value = valueFormatted || value;

    const handleCopyCommentClick = (comment: string) => {
        copyToClipboard(comment).then(() => {
            toastService.info(copyText ? `Copied ${copyText} to clipboard.` : 'Copied to clipboard');
        });
    };

    return (
        <div style={containerStyle(allowCopy, centered)} data-test-id={dataId} data-testid={dataTestId}>
            {allowCopy && value?.length > 0 && (
                <FontAwesomeIcon
                    style={{ color: Color.VIVLI_LIGHT_BLUE, cursor: 'pointer', alignSelf: 'start', marginTop: '2px' }}
                    icon={faCopy}
                    onClick={() => handleCopyCommentClick(_value)}
                    title={copyText ? `Copy ${copyText} to clipboard` : 'Copy to clipboard'}
                />
            )}
            {!onClick && (
                <HelpTextView value={titleText || _value} scrollable={scrollable} truncate={truncate}>
                    {_value}
                </HelpTextView>
            )}
            {onClick && (
                <div style={linkContainerStyle(scrollable)}>
                    {showPaperclipIcon && (
                        <FontAwesomeIcon
                            style={{ color: Color.VIVLI_LIGHT_BLUE, alignSelf: 'start', marginTop: '3px' }}
                            icon={faPaperclip}
                        />
                    )}
                    <div style={linkStyle} onClick={onClick}>
                        {_value}
                    </div>
                </div>
            )}
        </div>
    );
};
